import React, { useState } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileCheck, Plus, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { SubscriptionRequired } from "@/components/ui/subscription-required";
import { useBills } from "@/hooks/use-bills";
import { CreateBillDialog } from "@/components/expenses/create-bill-dialog";

export default function BillsPage() {
  const { toast } = useToast();
  const { currentBusiness: selectedBusiness } = useCurrentBusiness();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { bills, isLoading, refetch } = useBills(selectedBusiness?.id || 0);

  if (!selectedBusiness) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh] flex-col">
          <FileCheck className="h-16 w-16 text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Business Selected</h2>
          <p className="text-gray-600 mb-4">Please select or create a business to manage bills.</p>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Layout>
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          {/* Page header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Bills</h1>
              <p className="mt-1 text-sm text-neutral-500">
                Manage and track all your business bills in one place
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex space-x-3">
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Bill
              </Button>
            </div>
          </div>

          {/* Bills tabs */}
          <Tabs defaultValue="all" className="mt-6">
            <TabsList className="mb-6">
              <TabsTrigger value="all">All Bills</TabsTrigger>
              <TabsTrigger value="draft">Draft</TabsTrigger>
              <TabsTrigger value="open">Open</TabsTrigger>
              <TabsTrigger value="overdue">Overdue</TabsTrigger>
              <TabsTrigger value="paid">Paid</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all">
              <Card>
                <CardHeader>
                  <CardTitle>All Bills</CardTitle>
                  <CardDescription>View and manage all your bills</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                    <div className="text-center">
                      <FileCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900">No bills yet</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Get started by creating a new bill
                      </p>
                      <div className="mt-6">
                        <Button onClick={() => setIsCreateDialogOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          New Bill
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Other tab contents would be similar */}
            <TabsContent value="draft">
              <Card>
                <CardHeader>
                  <CardTitle>Draft Bills</CardTitle>
                  <CardDescription>Bills that haven't been finalized</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                    <div className="text-center">
                      <p className="text-gray-500">No draft bills found</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Similar for other tabs */}
          </Tabs>
        </div>
      </Layout>
      
      {/* Dialog component */}
      {selectedBusiness && (
        <CreateBillDialog 
          isOpen={isCreateDialogOpen} 
          onClose={() => setIsCreateDialogOpen(false)} 
          businessId={selectedBusiness.id} 
        />
      )}
    </>
  );
}